import type { BrowserOptions } from '@sentry/react';

// What Sentry gets of a URL: no query string and no fragment. A company
// activation link carries its token, the company's name and the user's id in
// its query (navigation/routes.ts, CompanyLogin…), and so does the request for
// the companies its contact may merge into, with what they searched for
// (CompanyService.ts, getCompaniesEligible); the lists' searches put what was
// searched for in theirs (OrdersService.ts, getOrders). With a link's token,
// anyone who can read Sentry could finish that activation with their own
// account until the link is used. The cost: no query detail when debugging.
export const withoutQuery = (url: string): string => url.split(/[?#]/)[0];

const scrubKeys = (
  record: Record<string, unknown> | undefined,
  keys: string[]
) => {
  if (!record) return;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') record[key] = withoutQuery(value);
  }
};

// A span's name that is a URL: a request's ("GET https://…", by its op, whatever
// URL it was opened with) and a resource's ("/static/js/main.js" on the page's
// origin, the whole URL on another). Any other name is left as it is: a click's
// is its element's CSS selector, where "#" starts an id, not a fragment.
const URL_NAME = /^(?:[A-Z]+ )?(?:[a-z][a-z\d+.-]*:\/\/|\/)/;
const isUrlName = (op: string | undefined, name: string) =>
  op === 'http.client' || URL_NAME.test(name);

// An event's page: its URL, and the page that linked to it.
const scrubRequest = (
  request: { url?: string; headers?: object } | undefined
) => {
  if (!request) return;
  if (request.url) request.url = withoutQuery(request.url);
  scrubKeys(request.headers as Record<string, unknown> | undefined, [
    'Referer',
  ]);
};

// The four places Sentry 8 puts a URL in what it sends.
export const sentryUrlScrubbing: Pick<
  BrowserOptions,
  'beforeBreadcrumb' | 'beforeSendSpan' | 'beforeSend' | 'beforeSendTransaction'
> = {
  // Navigation (from, to), XHR and fetch (url) breadcrumbs, when recorded.
  beforeBreadcrumb: (breadcrumb) => {
    scrubKeys(breadcrumb.data, ['url', 'from', 'to']);
    return breadcrumb;
  },
  // A span named by a URL (a request, a resource), and its URL attributes.
  // Sentry runs this on a transaction's spans, not on the transaction itself.
  beforeSendSpan: (span) => {
    if (span.description && isUrlName(span.op, span.description)) {
      span.description = withoutQuery(span.description);
    }
    scrubKeys(span.data, ['url', 'http.url']);
    // Not set by Sentry 8.33; a newer version may put the query and the
    // fragment there.
    if (span.data) {
      delete span.data['http.query'];
      delete span.data['http.fragment'];
    }
    return span;
  },
  beforeSend: (event) => {
    scrubRequest(event.request);
    return event;
  },
  beforeSendTransaction: (event) => {
    scrubRequest(event.request);
    // The transaction's own span: the URL of what the page's largest contentful
    // paint showed (an image's, say).
    scrubKeys(event.contexts?.trace?.data, ['lcp.url']);
    return event;
  },
};
